import { ProjectCard } from "@/components/workspace/project-card";
import type { WorkspaceFolder, WorkspaceProject } from "@/components/workspace/workspace-types";
import { useWorkspaceData } from "@/hooks/use-workspace-data";
/**
 * List view — single-column list of project rows. Less visual density than
 * the gallery; useful for power users with many projects.
 */
import { Folder } from "lucide-react";

export interface WorkspaceListViewProps {
  projects: WorkspaceProject[];
  folders: WorkspaceFolder[];
  onSelectFolder: (folderId: string) => void;
}

export function WorkspaceListView({
  projects,
  folders,
  onSelectFolder,
}: WorkspaceListViewProps): JSX.Element {
  const flags = useWorkspaceData((s) => s.flags);

  if (folders.length === 0 && projects.length === 0) {
    return (
      <div className="rounded-lg border border-dashed border-border bg-muted/30 p-8 text-center text-sm text-muted-foreground">
        No projects yet. {flags.projects_writable ? 'Click "New project" to get started.' : ""}
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {folders.map((folder) => (
        <button
          type="button"
          key={folder.id}
          onClick={() => onSelectFolder(folder.id)}
          className="flex w-full items-center gap-3 rounded-md border border-border bg-card px-3 py-2 text-left hover:bg-accent/40"
        >
          <Folder className="h-4 w-4 text-primary" />
          <span className="flex-1 truncate text-sm font-medium">{folder.name}</span>
          <span className="text-xs text-muted-foreground">
            {folder.project_count} project{folder.project_count === 1 ? "" : "s"}
          </span>
        </button>
      ))}
      {projects.map((project) => (
        <ProjectCard key={project.id} project={project} view="list" />
      ))}
    </div>
  );
}
